import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  DeleteInvitationTicketingUserValidationSchema,
  TDeleteInvitationTicketingUserValidation,
  useDeleteInvitationTicketingUserMutation,
} from '../../api/delete-invitation-ticketing-user.api';
import { GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY } from '../../api/get-list-invitation-ticketing-user.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_DELETE_INVITATION_TICKETING_USER = {
  DELETE_INVITATION_TICKETING_USER_ERROR: 'Delete invitation failed!',
  DELETE_INVITATION_TICKETING_USER_SUCCESS: 'Invitation deleted successfully!',
};

export type TDeleteInvitationTicketingUserModalType = 'DeleteInvitationTicketingUserModal';
export interface IDeleteInvitationTicketingUserModalProps {
  invitationId: string;
}
const DeleteInvitationTicketingUserModal = memo((props: IDeleteInvitationTicketingUserModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteInvitationTicketingUserValidation>({
    resolver: zodResolver(DeleteInvitationTicketingUserValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteInvitationTicketingUser = useDeleteInvitationTicketingUserMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) ||
          TOAST_MESSAGES_DELETE_INVITATION_TICKETING_USER.DELETE_INVITATION_TICKETING_USER_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY] });
        modalActions.closeModal('DeleteInvitationTicketingUserModal');
        showToast({
          message: TOAST_MESSAGES_DELETE_INVITATION_TICKETING_USER.DELETE_INVITATION_TICKETING_USER_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteInvitationTicketingUserModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteInvitationTicketingUserModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteInvitationTicketingUser.isPending;
  }, [mutationDeleteInvitationTicketingUser.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteInvitationTicketingUser.isPending;
  }, [mutationDeleteInvitationTicketingUser.isPending]);

  // EFFECTS
  useEffect(() => {
    if (props?.invitationId) {
      form.reset({
        invitationId: props.invitationId,
      });
    }
  }, [props?.invitationId, form]);

  // HANDLERS
  const handleSubmitForm = useCallback(
    (data: TDeleteInvitationTicketingUserValidation) => {
      mutationDeleteInvitationTicketingUser.mutate({
        invitationId: data?.invitationId || '',
      });
    },
    [mutationDeleteInvitationTicketingUser]
  );

  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteInvitationTicketingUserModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenDeleteInvitationTicketingUserModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Delete Invitation</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="delete-invitation-ticketing-user-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this invitation?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-invitation-ticketing-user-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteInvitationTicketingUserModal.displayName = 'DeleteInvitationTicketingUserModal';

export default DeleteInvitationTicketingUserModal;
